<template>
  <div class="flex flex-col gap-2">
    <div v-if="modelValue.length === 0" class="flex justify-center">
      <CButton size="small" @click="addFilter">
        <template #icon>
          <CIcon icon="plus" size="16" />
        </template>
        {{ t('canvasUI.components.CQueryBuilder.addFilter') }}
      </CButton>
    </div>
    <template v-else>
      <div class="grid grid-cols-[200px_200px_1fr_auto] gap-2 items-end">
        <div class="text-sm text-gray-500">{{ t('canvasUI.components.CQueryBuilder.attribute') }}</div>
        <div class="text-sm text-gray-500">{{ t('canvasUI.components.CQueryBuilder.operator') }}</div>
        <div class="text-sm text-gray-500">{{ t('canvasUI.components.CQueryBuilder.value') }}</div>
        <div class="w-[84px]" />
      </div>

      <div v-for="(filter, index) in modelValue" :key="index" class="flex gap-2 items-center">
        <NSelect v-model:value="filter.property" class="w-[200px]" :options="attributes" />

        <NSelect v-model:value="filter.operator" class="w-[200px]" :options="operatorsList" />

        <NInput
          v-if="!isBooleanOperator(filter.operator)"
          v-model:value="filter.value as string"
          class="flex-1"
          placeholder=""
        />

        <!-- 2024-12-05:This will be either completely removed or repurposed at a later stage -->
        <!-- <NSelect
          v-if="isMultiValueOperator(filter.operator)"
          v-model:value="filter.value"
          class="flex-1"
          :consistent-menu-width="false"
          filterable
          multiple
          placeholder=""
          :show="false"
          :show-arrow="false"
          tag
        /> -->

        <div class="flex gap-2">
          <NButton circle size="small" @click="addFilter">
            <template #icon>
              <CIcon icon="plus" size="16" />
            </template>
          </NButton>
          <NButton circle secondary size="small" type="error" @click="removeFilter(index)">
            <template #icon>
              <CIcon icon="trash" size="16" />
            </template>
          </NButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { NButton, NInput, NSelect } from 'naive-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import type { QueryAttribute, QueryOperator } from '@lib/types/query-builder.types'
import { QUERY_OPERATORS } from '@lib/constants/query-builder.const'

const props = withDefaults(
  defineProps<{
    modelValue: QueryAttribute[]
    attributes: { label: string; value: string }[]
    operators?: typeof QUERY_OPERATORS
  }>(),
  {
    operators: () => QUERY_OPERATORS,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: QueryAttribute[]]
}>()

const { t } = useI18n()

const operators = computed(() =>
  props.operators.map((op) => ({
    value: op,
    label: getDefaultOperatorTranslations(op),
  })),
)

const operatorsList = computed(() =>
  operators.value.map((op) => ({
    label: op.label,
    value: op.value,
  })),
)

function addFilter() {
  emit('update:modelValue', [
    ...props.modelValue,
    {
      property: props.attributes[0]?.value ?? '',
      operator: 'contains',
      value: '',
    },
  ])
}

function removeFilter(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  )
}

function isBooleanOperator(operator: QueryOperator): boolean {
  return ['true', 'false'].includes(operator)
}

function getDefaultOperatorTranslations(operator: QueryOperator): string {
  switch (operator) {
    case 'contains':
      return t('canvasUI.components.CQueryBuilder.operators.contains')
    case 'not_contains':
      return t('canvasUI.components.CQueryBuilder.operators.notContains')
    case 'true':
      return t('canvasUI.components.CQueryBuilder.operators.true')
    case 'false':
      return t('canvasUI.components.CQueryBuilder.operators.false')
    case 'eq':
      return t('canvasUI.components.CQueryBuilder.operators.equals')
    case 'neq':
      return t('canvasUI.components.CQueryBuilder.operators.notEquals')
    case 'gt':
      return '>'
    case 'gte':
      return '>='
    case 'lt':
      return '<'
    case 'lte':
      return '<='
    default:
      return ''
  }
}
</script>
